// deps
import React, { memo, useCallback } from "react";

// internal-deps
import { MenuItemsCalloutInfo } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { MENU_ITEMS_CALLOUT_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";

// components
import HalfCardTrigger from "./components/half-card-trigger";

//styles
import { CalloutContainer, Container } from "./MenuItemsCallout.style";

export interface MenuItemsCalloutProps {
    calloutInfo?: MenuItemsCalloutInfo;
    analyticsPayload: {
        onItemsBannerImpression?: (calloutMessage?: string) => void;
        onItemsBannerClick?: (calloutMessage?: string) => void;
        onHalfCardBannerImpression?: VoidFunction;
        onHalfCardBannerCtaClick?: VoidFunction;
    };
}

function MenuItemsCallout({ calloutInfo, analyticsPayload }: MenuItemsCalloutProps) {
    const { onItemsBannerImpression, onItemsBannerClick, onHalfCardBannerImpression, onHalfCardBannerCtaClick } =
        analyticsPayload || {};

    const onHalfCardTriggerClick = useCallback(() => {
        onItemsBannerClick?.(calloutInfo?.message);
    }, [calloutInfo?.message, onItemsBannerClick]);

    const itemsBannerImpression = useCallback(() => {
        onItemsBannerImpression?.(calloutInfo?.message);
    }, [calloutInfo?.message, onItemsBannerImpression]);

    const onHalfCardCtaClick = useCallback(() => {
        onHalfCardBannerCtaClick?.();
    }, [onHalfCardBannerCtaClick]);

    const onHalfCardViewImpression = useCallback(() => {
        onHalfCardBannerImpression?.();
    }, [onHalfCardBannerImpression]);

    if (isEmpty(calloutInfo?.message)) {
        return null;
    }

    return (
        <ElementVisibilitySensor senseOnce onVisibilityChange={itemsBannerImpression}>
            <Container data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_WIDGET}>
                <CalloutContainer backgroundColor={calloutInfo?.background_color}>
                    <Text
                        variant="V2.body3.neutral.medium"
                        color="text_Med_Emphasis"
                        data-testid={MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_MESSAGE}
                    >
                        {calloutInfo?.message}

                        <HalfCardTrigger
                            calloutInfo={calloutInfo}
                            onHalfCardTriggerClick={onHalfCardTriggerClick}
                            onHalfCardViewImpression={onHalfCardViewImpression}
                            onHalfCardCtaClick={onHalfCardCtaClick}
                        />
                    </Text>
                </CalloutContainer>
            </Container>
        </ElementVisibilitySensor>
    );
}

export default memo(MenuItemsCallout);
